import { useState, useEffect } from 'react'
import { Card, CardBody, CardHeader, Spinner, Button as HeroButton } from '@heroui/react'
import { Badge } from '@trafiflow/shared-ui'
import { useNavigate } from 'react-router-dom'
import { funnelApi } from '../../api'
import type { Funnel } from '../../types'

export function FunnelsOverview() {
  const [funnels, setFunnels] = useState<Funnel[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    async function load() {
      try {
        const response = await funnelApi.list()
        const sorted = (response.data ?? [])
          .slice()
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
          .slice(0, 8)
        setFunnels(sorted)
      } catch {
        // API failure — funnels overview will show empty state
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return (
      <Card>
        <CardBody className="flex items-center justify-center py-8">
          <Spinner size="sm" color="primary" />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between px-6 pt-5 pb-0">
        <h2 className="text-lg font-semibold text-foreground">Funnels Overview</h2>
        <HeroButton
          variant="light"
          color="primary"
          size="sm"
          onPress={() => navigate('/funnels')}
        >
          View All
        </HeroButton>
      </CardHeader>
      <CardBody className="px-6 pb-5">
        {funnels.length === 0 ? (
          <p className="text-sm text-default-500 text-center py-4">No funnels yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-default-500 uppercase border-b border-default-200">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Steps</th>
                  <th className="py-2">Priority</th>
                </tr>
              </thead>
              <tbody>
                {funnels.map((funnel) => (
                  <tr
                    key={funnel.id}
                    className="border-b border-default-100 hover:bg-content2 cursor-pointer transition-colors"
                    onClick={() => navigate(`/funnels/${funnel.id}`)}
                  >
                    <td className="py-2.5 pr-4 font-medium text-foreground">{funnel.name}</td>
                    <td className="py-2.5 pr-4">
                      <Badge variant={funnel.isActive ? 'success' : 'default'}>
                        {funnel.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4 text-default-600">{funnel.funnelType || 'standard'}</td>
                    <td className="py-2.5 pr-4 text-default-600">{funnel.steps?.length ?? 0}</td>
                    <td className="py-2.5 text-default-600">{funnel.priority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
